import { EMPTY_OBJECT } from '@unifyapps/carbon/consts/empty';
import { getComponentStylesOutput } from '@unifyapps/carbon/theme/utils/getComponentStylesOutput';
import type { ThemeProperties } from '@unifyapps/defs/theme/types';
import { AppThemeMode } from '@unifyapps/defs/types/interface';
import { scopeComponentStylesForMatrix, stringifyThemeForMatrix } from '../views/builder/utils';
import { getCustomFontFaceCss } from './getCustomFontFaceCss';
import { resolveThemeTokens } from './resolveThemeTokens';

/**
 * Builds the full matrix StyleOverride CSS string: legacy output plus
 * light/dark theme tokens. Routes through resolveThemeTokens so legacy
 * storage shapes (flat-only, dark-nested) render correctly with mirrored
 * tokens on the opposite scheme.
 */
export function buildMatrixThemeStyleString(
  props: Partial<
    Pick<
      ThemeProperties,
      'cssVars' | 'components' | 'styleOverrides' | 'defaultScheme' | 'customFonts'
    >
  > & {
    legacyThemeOutput?: string;
  },
): string {
  const {
    legacyThemeOutput,
    cssVars = EMPTY_OBJECT,
    components,
    styleOverrides,
    defaultScheme = 'both',
    customFonts,
  } = props;
  const { light: lightCssVars, dark: darkCssVars } = resolveThemeTokens({
    cssVars,
    defaultScheme,
  });
  const lightOutput = stringifyThemeForMatrix(lightCssVars, AppThemeMode.Light);
  const darkOutput = stringifyThemeForMatrix(darkCssVars, AppThemeMode.Dark);
  const componentStyles = scopeComponentStylesForMatrix(getComponentStylesOutput(components));
  // @font-face rules for the theme's uploaded custom fonts. At-rules must live at the top level (not
  // inside the scoped `.interface-theme {…}` blocks), so they go first; without them a font-family
  // token referencing a custom family has no registered face and falls back to a system font.
  const customFontFaces = getCustomFontFaceCss(customFonts);
  /**
   * use theme stored in interface record (may have navigation styles)
   * injecting them first so that they are of less specificity,
   * so that if they are overriden in theme components,
   * they dont take precedence over the theme entity tokens
   * styleOverrides will have the highest priority
   */
  return customFontFaces
    .concat(legacyThemeOutput ?? '')
    .concat(lightOutput)
    .concat(darkOutput)
    .concat(componentStyles)
    .concat(styleOverrides ?? '');
}
